import { firstValueFrom } from 'rxjs';
import { BuildingService, IdReferenceDto, TenantRoleService } from '@artemis-software/wr-api';
import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { createValidator } from '@sonofabit/ng-core';

export class UniqueInternalNumberValidator {
  static createValidator(buildingService: BuildingService, buildingId?: string): AsyncValidatorFn {
    return async (control: AbstractControl): Promise<ValidationErrors | null> => {
      const internalNumber = control.value;
      if (!internalNumber) {
        return null;
      }
      const isUnique = await firstValueFrom(buildingService.isInternalNumberUnique({
        internalNumber: internalNumber,
        buildingId: buildingId,
      }));
      if (!isUnique) {
        return { duplicateInternalNumber: true };
      }
      return null;
    };
  }
}

export class checkRoleWeightValidator {
  static createValidator(tenantRoleService: TenantRoleService, tenantRoleId: string): AsyncValidatorFn {
    return async (control: AbstractControl): Promise<ValidationErrors | null> => {
      const weight = control.value;
      if (!weight) {
        return null;
      }
      const roles = await firstValueFrom(tenantRoleService.getTenantRolesWithSameWeight(weight, tenantRoleId));
      if (roles.length > 0) {
        return { duplicateWeight: true };
      }
      return null;
    };
  }
}

export class ArrayRequiredValidator {
  static createValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const array = control.value as unknown[];
      const hasValue = Array.isArray(array)
        && array.some((v) => v !== null && v !== undefined && v !== '');
      return hasValue ? null : { required: true };
    };
  }
}

export class EntityReferenceRequiredValidator {
  static createValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const entityReference: IdReferenceDto = control.value;
      if (!entityReference.value) {
        return { required: true };
      }
      return null;
    };
  }
}

export class AddressCompleteValidator {
  static createValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const address = control.value;
      if (!address || !address.zipCode || !address.city || !address.state) {
        return { addressIncomplete: true };
      }
      return null;
    };
  }
}

const REISENBAUER_GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const reisenbauerGuidValidator = createValidator<string | null | undefined, true, []>('reisenbauerGuid', {
  validator: (control) => {
    const value = control.value;
    if (!value) {
      return null;
    }
    // Nur auf berührten Feldern prüfen — das spiegelt SensorCrudService, das ausschließlich
    // geänderte Werte validiert (`takeIf { it != entity.functionBlockId }`). Ohne diese Bedingung
    // sperrt eine einzige Legacy-ID das gesamte Block-Formular, inklusive Name, Farbe und aller
    // anderen Sensoren — und das ungültige Feld sieht der Nutzer erst, wenn er den betroffenen
    // Sensor in der Liste anklickt.
    if (!control.dirty) {
      return null;
    }
    // Mirrors Sensor.parseReisenbauerGuid: trim, strip wrapping braces/quotes, keep the first
    // 36 characters, then match. Trailing text is tolerated on purpose — SQL Server ignores it
    // and two production sensors rely on it ('36a20365-… Ext. Freigabe').
    return REISENBAUER_GUID.test(value.trim().replace(/^[{"']+|[}"']+$/g, '').slice(0, 36)) ? null : true;
  },
  message: (_value, label) => `${label} muss eine GUID sein, z. B. 4f1c8ba0-72d6-4a1e-9c35-1e0b7d2af864.`,
});

export class MinDateValidator {
  static createValidator(minimumDate: Date | undefined): ValidatorFn {
    const hasValidMinimumDate = minimumDate instanceof Date && !isNaN(minimumDate.getTime());
    const normalizedMinimumDate = hasValidMinimumDate ? new Date(minimumDate!.getFullYear(), minimumDate!.getMonth(), minimumDate!.getDate()) : null;

    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as Date | undefined | null;
      if (!(value instanceof Date) || isNaN(value.getTime()) || !normalizedMinimumDate) {
        return null;
      }

      const normalizedControlDate = new Date(value.getFullYear(), value.getMonth(), value.getDate());
      return normalizedControlDate.getTime() >= normalizedMinimumDate.getTime() ? null : { minDate: true };
    };
  }
}
